// src/components/layout/Header.jsx
import { useState, memo, useCallback, useMemo, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getAvatarById } from "../../data/avataresPredefinidos";
import logo from '../../assets/vadieg_logo.svg';
import ChangelogBadge from '../common/ChangelogBadge';
import NotificacionesBadge from '../common/NotificacionesBadge';
import api from '../../api/client';

const PAGE_TITLES = {
  '/': 'Inicio',
  '/tickets': 'Tickets',
  '/tickets/nuevo': 'Nuevo Ticket',
  '/ordenes/lista': 'Órdenes de Servicio',
  '/ordenes/nueva': 'Nueva Orden',
  '/hallazgos': 'Hallazgos y Desvíos',
  '/hallazgos/nuevo': 'Nuevo Hallazgo',
  '/ausentismo': 'Ausentismo',
  '/ausentismo/nuevo': 'Nuevo Ausentismo',
  '/servicios': 'Servicios Generales',
  '/servicios/nueva': 'Nueva Solicitud',
  '/novedades': 'Novedades',
  '/agenda': 'Agenda',
  '/contactos': 'Contactos',
  '/empresa': 'Nuestra Empresa',
  '/biblioteca': 'Biblioteca',
  '/capacitacion': 'Capacitación',
  '/dashboard-ejecutivo': 'Dashboard Ejecutivo',
  '/gerencial': 'Gerencial',
  '/reportes': 'Reportes',
  '/equipos-informaticos': 'Equipos Informáticos',
  '/admin': 'Administración',
  '/perfil': 'Mi Perfil',
  '/configuracion': 'Configuración',
  '/remitos': 'Remitos Internos',
  '/dashboard': 'Dashboard',
};

function getPageTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  const found = Object.entries(PAGE_TITLES).find(([k]) => k !== '/' && pathname.startsWith(k));
  return found ? found[1] : '';
}

function Header() {
  const { usuario, logout, tieneAcceso, esAdmin: esAdminFn, token } = useAuth();
  const colors = {
    navBg: '#0d1117',
    border: '#1e293b',
    navInputBg: '#161b2e',
    navInputFocusBg: '#1e293b',
    navInputBorder: '#1e293b',
    borderFocus: '#f59e0b',
    text: '#f1f5f9',
    textMuted: '#64748b',
    dropdownBg: '#0d1117',
    dropdownBorder: '#1e293b',
    dropdownShadow: '0 20px 60px rgba(0,0,0,0.5)',
    dropdownTextMuted: '#64748b',
    dropdownText: '#f1f5f9',
    dropdownHoverBg: 'rgba(255,255,255,0.05)',
  };
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null = sin buscar
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounce = useRef(null);
  const searchRef = useRef(null);

  // Instalar PWA
  const [pwaPrompt, setPwaPrompt] = useState(null);
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPwaPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Preferencias de accesos ocultos (sincronizado con LandingPage vía custom event)
  const [accesosOcultos, setAccesosOcultos] = useState([]);
  useEffect(() => {
    if (!usuario?.id) return;
    try {
      const key = `vadieg_accesos_ocultos_${usuario.id}`;
      setAccesosOcultos(JSON.parse(localStorage.getItem(key) || '[]'));
    } catch { setAccesosOcultos([]); }
  }, [usuario?.id]);
  useEffect(() => {
    const handler = (e) => setAccesosOcultos(e.detail.ocultos || []);
    window.addEventListener('vadieg-accesos-changed', handler);
    return () => window.removeEventListener('vadieg-accesos-changed', handler);
  }, []);

  const handleInstallPWA = useCallback(async () => {
    if (!pwaPrompt) return;
    pwaPrompt.prompt();
    const { outcome } = await pwaPrompt.userChoice;
    if (outcome === 'accepted') setPwaPrompt(null);
  }, [pwaPrompt]);

  const styles = {
    navbar: {
      background: colors.navBg,
      padding: "0.4rem 1rem",
      borderBottom: `1px solid ${colors.border}`,
      position: "sticky",
      top: 0,
      zIndex: 1000,
      minHeight: 48,
    },
    logoContainer: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    logo: {
      height: "clamp(32px, 8vw, 42px)",
    },
    rightSection: {
      display: "flex",
      alignItems: "center",
      gap: "clamp(0.25rem, 2vw, 0.75rem)",
    },
    searchContainer: {
      display: "flex",
      alignItems: "center",
      background: searchFocused ? colors.navInputFocusBg : colors.navInputBg,
      border: `1px solid ${searchFocused ? colors.borderFocus : colors.navInputBorder}`,
      borderRadius: "8px",
      padding: "0.25rem 0.5rem",
      transition: "all 0.2s ease",
    },
    searchInput: {
      background: "transparent",
      border: "none",
      outline: "none",
      color: colors.text,
      fontSize: "clamp(0.75rem, 2vw, 0.85rem)",
      width: "clamp(100px, 20vw, 180px)",
      padding: "0.25rem 0.5rem",
    },
    searchIcon: {
      color: colors.textMuted,
      fontSize: "0.9rem",
    },
    userBtn: {
      display: "flex",
      alignItems: "center",
      gap: "clamp(0.35rem, 2vw, 0.75rem)",
      padding: "0.3rem 0.5rem 0.3rem 0.3rem",
      borderRadius: "10px",
      background: colors.navInputBg,
      border: `1px solid ${colors.navInputBorder}`,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    avatar: {
      width: "clamp(28px, 7vw, 32px)",
      height: "clamp(28px, 7vw, 32px)",
      borderRadius: "8px",
      background: "linear-gradient(135deg, #fbbf24 0%, #ea580c 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "clamp(0.7rem, 2vw, 0.8rem)",
      fontWeight: 700,
      color: "#000",
      flexShrink: 0,
    },
    avatarImage: {
      width: "clamp(28px, 7vw, 32px)",
      height: "clamp(28px, 7vw, 32px)",
      borderRadius: "8px",
      objectFit: "cover",
      flexShrink: 0,
    },
    userName: {
      color: "#ffffff",
      fontSize: "clamp(0.75rem, 2vw, 0.85rem)",
      fontWeight: 500,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "clamp(80px, 15vw, 120px)",
    },
    dropdown: {
      position: "fixed",
      background: colors.dropdownBg,
      border: `1px solid ${colors.dropdownBorder}`,
      borderRadius: "12px",
      padding: "0.5rem",
      minWidth: "220px",
      maxHeight: "70vh",
      overflowY: "auto",
      boxShadow: colors.dropdownShadow,
      animation: "fadeIn 0.2s ease",
      zIndex: 1050,
    },
    dropdownHeader: {
      color: colors.dropdownTextMuted,
      fontSize: "0.7rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      padding: "0.5rem 0.75rem 0.25rem",
    },
    dropdownItem: {
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      padding: "0.5rem 0.75rem",
      borderRadius: "8px",
      color: colors.dropdownText,
      fontSize: "0.85rem",
      cursor: "pointer",
      transition: "all 0.15s ease",
      border: "none",
      background: "transparent",
      width: "100%",
      textAlign: "left",
      textDecoration: "none",
    },
    dropdownDivider: {
      height: 1,
      background: colors.dropdownBorder,
      margin: "0.5rem 0",
    },
    logoutItem: {
      color: "#ef4444",
    },
    adminBadge: {
      background: "rgba(251, 191, 36, 0.2)",
      color: "#fbbf24",
      fontSize: "0.65rem",
      padding: "0.1rem 0.4rem",
      borderRadius: "4px",
      marginLeft: "auto",
    },
    overlay: {
      position: "fixed",
      inset: 0,
      zIndex: 1040,
    },
    chevron: {
      width: 12,
      height: 12,
      color: "rgba(255, 255, 255, 0.5)",
    },
  };

  const cssAnimation = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-8px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;

  // Admin: usa la función del contexto (soporta ROL-01 / org.admin)
  const esAdmin = esAdminFn();

  // Accesos dinámicos filtrados por permisos
  const accesosDisponibles = useMemo(() => {
    const todosLosAccesos = [
      { to: "/tickets",             icon: "🎫", label: "Tickets",              permiso: "shared.tickets:read:own" },
      { to: "/partes-obra",         icon: "🏗️", label: "Producción",           permisoAny: ["ops.machinist-logs:read:own","ops.machinist-logs:read:all","ops.machinist-logs:manage","ops.machinist-logs:create"] },
      { to: "/equipos-informaticos",icon: "💻", label: "Equipos Informáticos", soloAdmin: true },
      { to: "/ordenes/lista",       icon: "📋", label: "Órdenes de servicio",  permiso: "ops.work-orders:read" },
      { to: "/hallazgos",           icon: "⚠️", label: "Hallazgos y Desvíos", permisoAny: ["hse.findings:read","hse.findings:write","hse.findings:manage"] },
      { to: "/agenda",              icon: "📅", label: "Agenda",               permiso: "shared.agenda:read" },
      { to: "/contactos",           icon: "📇", label: "Contactos",            permiso: "shared.directory:read" },
      { to: "/dashboard-ejecutivo", icon: "🎯", label: "Dashboard Ejecutivo",  permisoAny: ["reports.executive:read","exec.dashboard:read","exec.certifications:read"] },
      { to: "/dashboard",           icon: "📊", label: "Dashboard",            soloAdmin: true },
      { to: "/gerencial",           icon: "💰", label: "Gerencial",            permisoAny: ["exec.dashboard:manage","exec.reports:read","exec.reports:export"] },
      { to: "/reportes",            icon: "📈", label: "Reportes",             soloAdmin: true },
      { to: "/servicios",           icon: "🛠️", label: "Servicios Generales", permiso: null },
      { to: "/capacitacion",        icon: "🎓", label: "Capacitación",         permiso: "shared.training:read" },
      { to: "/ausentismo",          icon: "🏥", label: "Ausentismo",           permisoAny: ["hr.attendance:read","hr.attendance:write","hr.reports:read"] },
      // Remitos Internos: oculto por ahora (oct 2026)
      // { to: "/remitos",             icon: "📦", label: "Remitos Internos",     soloAdmin: true },
      { to: "/novedades",           icon: "📰", label: "Novedades",            permiso: null },
      { to: "/empresa",           icon: "🏢", label: "Nuestra Empresa",       permiso: null },
      { to: "/biblioteca",        icon: "📚", label: "Biblioteca",            permiso: null },
    ];

    return todosLosAccesos.filter(acceso => {
      if (accesosOcultos.includes(acceso.to)) return false;
      if (acceso.soloAdmin)  return esAdmin;
      if (acceso.permisoAny) return acceso.permisoAny.some(p => tieneAcceso(p));
      if (acceso.permiso)    return tieneAcceso(acceso.permiso);
      return true;
    });
  }, [tieneAcceso, accesosOcultos, esAdmin]);

  // Accesos de creación filtrados por permisos
  const accionesCrear = useMemo(() => {
    const todasLasAcciones = [
      { to: "/ordenes/nueva",   icon: "➕", label: "Nueva orden",             permiso: "ops.work-orders:create" },
      { to: "/tickets/nuevo",   icon: "➕", label: "Nuevo ticket",            permiso: "shared.tickets:create" },
      { to: "/hallazgos/nuevo", icon: "➕", label: "Nuevo hallazgo",          permiso: "hse.findings:write" },
      { to: "/servicios/nueva", icon: "➕", label: "Nueva solicitud servicio", permiso: null },
      { to: "/ausentismo/nuevo",icon: "➕", label: "Nuevo ausentismo",        permisoAny: ["hr.attendance:write","hr.reports:read"] },
    ];

    return todasLasAcciones.filter(accion => {
      if (accion.permisoAny) return accion.permisoAny.some(p => tieneAcceso(p));
      if (accion.permiso)    return tieneAcceso(accion.permiso);
      return true;
    });
  }, [tieneAcceso]);

  // Handlers optimizados con useCallback
  const handleLogout = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await api.post('/logout', { refreshToken: localStorage.getItem('refreshToken') || undefined }).catch(() => {});
      }
    } finally {
      logout();
      navigate('/login');
    }
  }, [navigate, logout]);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults(null);
  }, []);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(searchDebounce.current);
    if (!val.trim()) { setSearchResults(null); return; }
    searchDebounce.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await api.get(`/buscar?q=${encodeURIComponent(val.trim())}`);
        setSearchResults(data);
      } catch { setSearchResults({ hallazgos: [], novedades: [] }); }
      finally { setSearchLoading(false); }
    }, 320);
  }, []);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === "Escape") { clearSearch(); searchRef.current?.blur(); }
  }, [clearSearch]);

  const handleToggleDropdown = useCallback((e) => {
    if (!showDropdown) {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setShowDropdown(!showDropdown);
  }, [showDropdown]);

  const closeDropdown = useCallback(() => setShowDropdown(false), []);

  // Utilidades memoizadas
  const getInitials = useCallback((name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
  }, []);

  // Componente DropdownLink memoizado para evitar re-renders
  const DropdownLink = memo(({ to, icon, children, badge }) => (
    <Link
      to={to}
      style={styles.dropdownItem}
      onClick={closeDropdown}
      onMouseOver={(e) => e.currentTarget.style.background = colors.dropdownHoverBg}
      onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
    >
      <span>{icon}</span>
      {children}
      {badge && <span style={styles.adminBadge}>{badge}</span>}
    </Link>
  ));

  // Contenido del dropdown
  const dropdownContent = (
    <>
      <div style={styles.overlay} onClick={closeDropdown} />
      <div 
        style={{
          ...styles.dropdown,
          top: dropdownPos.top,
          right: dropdownPos.right,
        }}
      >
        {/* Sección Perfil */}
        <div style={styles.dropdownHeader}>Mi Cuenta</div>
        <DropdownLink to="/perfil" icon="👤">Mi perfil</DropdownLink>
        <DropdownLink to="/configuracion" icon="⚙️">Configuración</DropdownLink>

        {/* Accesos disponibles según permisos */}
        {accesosDisponibles.length > 0 && (
          <>
            <div style={styles.dropdownDivider}></div>
            <div style={styles.dropdownHeader}>Módulos</div>
            {accesosDisponibles.map((acceso, idx) => (
              <DropdownLink key={idx} to={acceso.to} icon={acceso.icon}>
                {acceso.label}
              </DropdownLink>
            ))}
          </>
        )}

        {/* Acciones de crear según permisos */}
        {accionesCrear.length > 0 && (
          <>
            <div style={styles.dropdownDivider}></div>
            <div style={styles.dropdownHeader}>Crear</div>
            {accionesCrear.map((accion, idx) => (
              <DropdownLink key={idx} to={accion.to} icon={accion.icon}>
                {accion.label}
              </DropdownLink>
            ))}
          </>
        )}


        {/* Panel Admin - Solo para admin */}
        {esAdmin && (
          <>
            <div style={styles.dropdownDivider}></div>
            <DropdownLink to="/admin" icon={
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            } badge="Admin">
              Panel de Administración
            </DropdownLink>
          </>
        )}

        {/* Cerrar sesión */}
        <div style={styles.dropdownDivider}></div>
        <button
          style={{...styles.dropdownItem, ...styles.logoutItem}}
          onClick={handleLogout}
          onMouseOver={(e) => e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)"}
          onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
        >
          <span>🚪</span> Cerrar sesión
        </button>
      </div>
    </>
  );

  return (
    <>
      <style>{cssAnimation}</style>
      <nav style={styles.navbar}>
        <div className="d-flex align-items-center w-100">
          {/* Logo — solo visible en mobile (el sidebar ya lo tiene en desktop) */}
          <Link to="/" style={styles.logoContainer} className="d-lg-none">
            <img src={logo} alt="VADIEG" style={styles.logo} />
          </Link>

          {/* Título de página — visible en desktop cuando el sidebar está activo */}
          {usuario && (
            <div className="d-none d-lg-flex align-items-center" style={{ gap: 8 }}>
              <span style={{ color: '#64748b', fontSize: '0.75rem' }}>›</span>
              <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.88rem', letterSpacing: '0.01em' }}>
                {getPageTitle(pathname) || 'VADIEG'}
              </span>
            </div>
          )}

          {/* Right section — marginLeft auto para siempre estar a la derecha */}
          <div style={{ ...styles.rightSection, marginLeft: 'auto' }}>
            
            {/* Buscador con resultados live */}
            {usuario && (
              <div style={{ position: 'relative' }} className="d-none d-sm-flex">
                <div style={styles.searchContainer}>
                  <span style={styles.searchIcon}>
                    {searchLoading
                      ? <span style={{ fontSize: '0.8rem', animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                      : '🔎'}
                  </span>
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Buscar avisos, novedades..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setTimeout(() => { setSearchFocused(false); }, 200)}
                    style={styles.searchInput}
                  />
                  {searchQuery && (
                    <button onClick={clearSearch} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: '0 4px', fontSize: '0.85rem' }}>✕</button>
                  )}
                </div>

                {/* Dropdown de resultados */}
                {searchResults && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 6,
                    background: colors.dropdownBg,
                    border: `1px solid ${colors.dropdownBorder}`,
                    borderRadius: 12,
                    minWidth: 320,
                    maxHeight: 420,
                    overflowY: 'auto',
                    boxShadow: colors.dropdownShadow,
                    zIndex: 2000,
                    animation: 'fadeIn 0.15s ease',
                  }}>
                    {/* Hallazgos */}
                    {searchResults.hallazgos?.length > 0 && (
                      <>
                        <div style={{ padding: '8px 14px 4px', fontSize: '0.68rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Avisos SST
                        </div>
                        {searchResults.hallazgos.map(h => (
                          <Link key={h.id} to={`/hallazgos/${h.id}`} onClick={clearSearch}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', color: colors.dropdownText, textDecoration: 'none', fontSize: '0.84rem' }}
                            onMouseOver={e => e.currentTarget.style.background = colors.dropdownHoverBg}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontSize: '1rem' }}>⚠️</span>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.8rem', color: colors.text }}>{h.numero_hallazgo}</div>
                              <div style={{ fontSize: '0.75rem', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
                                {h.descripcion || h.titulo || '—'}
                              </div>
                            </div>
                            <span style={{ marginLeft: 'auto', fontSize: '0.65rem', padding: '2px 6px', borderRadius: 4, background: h.estado === 'CERRADO' ? 'rgba(22,163,74,0.15)' : 'rgba(239,68,68,0.15)', color: h.estado === 'CERRADO' ? '#16a34a' : '#ef4444', whiteSpace: 'nowrap' }}>
                              {h.estado?.replace('_', ' ')}
                            </span>
                          </Link>
                        ))}
                      </>
                    )}

                    {/* Novedades */}
                    {searchResults.novedades?.length > 0 && (
                      <>
                        <div style={{ padding: '8px 14px 4px', fontSize: '0.68rem', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', borderTop: searchResults.hallazgos?.length ? `1px solid ${colors.dropdownBorder}` : 'none' }}>
                          Novedades
                        </div>
                        {searchResults.novedades.map(n => (
                          <Link key={n.id} to={`/novedades/${n.id}`} onClick={clearSearch}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', color: colors.dropdownText, textDecoration: 'none', fontSize: '0.84rem' }}
                            onMouseOver={e => e.currentTarget.style.background = colors.dropdownHoverBg}
                            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontSize: '1rem' }}>📰</span>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: '0.8rem', color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>{n.titulo}</div>
                              {n.tag && <div style={{ fontSize: '0.7rem', color: '#f59e0b' }}>{n.tag}</div>}
                            </div>
                          </Link>
                        ))}
                      </>
                    )}

                    {/* Sin resultados */}
                    {!searchResults.hallazgos?.length && !searchResults.novedades?.length && (
                      <div style={{ padding: '20px 14px', textAlign: 'center', color: colors.textMuted, fontSize: '0.84rem' }}>
                        Sin resultados para "{searchQuery}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Botón instalar PWA */}
            {pwaPrompt && (
              <button
                onClick={handleInstallPWA}
                className="d-none d-md-flex"
                style={{ alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.12)', color: '#f59e0b', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 16V4M8 12l4 4 4-4"/><path d="M4 20h16"/></svg>
                Instalar app
              </button>
            )}

            {usuario && (
              <div className="d-none d-md-block">
                <NotificacionesBadge />
              </div>
            )}

            {esAdmin && <AlertasBadge token={token} />}

            {!usuario && (
              <Link
                to="/login"
                style={{
                  padding: "0.3rem 0.75rem",
                  borderRadius: "8px",
                  background: "rgba(251,191,36,0.12)",
                  border: "1px solid rgba(251,191,36,0.3)",
                  textDecoration: "none",
                  color: "#fbbf24",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                }}
              >
                Iniciar sesión
              </Link>
            )}
          </div>
        </div>
      </nav>

    </>
  );
}

// ── Campana de alertas de partes (solo admin) ─────────────────────────────────
function AlertasBadge({ token }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const cargar = () => {
      if (!token) return;
      fetch(`${process.env.REACT_APP_API_URL}/alertas-partes/count`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(d => { if (d.ok) setCount(d.total); })
        .catch(() => {});
    };
    cargar();
    const t = setInterval(cargar, 5 * 60 * 1000); // cada 5 min
    return () => clearInterval(t);
  }, [token]);

  return (
    <Link to="/admin/alertas-partes"
      title={count > 0 ? `${count} alerta${count !== 1 ? 's' : ''} activa${count !== 1 ? 's' : ''}` : 'Sin alertas activas'}
      style={{ position: 'relative', textDecoration: 'none', display: 'flex', alignItems: 'center', padding: '4px 6px', borderRadius: 6 }}>
      <span style={{ fontSize: '1.2rem' }}>{count > 0 ? '🔔' : '🔕'}</span>
      {count > 0 && (
        <span style={{
          position: 'absolute', top: -4, right: -4,
          background: '#ef4444', color: '#fff',
          fontSize: '0.6rem', fontWeight: 800,
          borderRadius: 10, padding: '1px 5px', minWidth: 16, textAlign: 'center',
        }}>{count}</span>
      )}
    </Link>
  );
}

// Exportar Header memoizado para evitar re-renders innecesarios
export default memo(Header);